import { ModuleDefectObjectType, type ModuleDefectHistoryEntry } from "@eisox/api/core-api";
import {
  isValidAirHandlerUnitDefect,
  isValidBoilerroomDefect,
  translateDefect,
  type AirHandlerUnitDefectType,
  type AirHandlerUnitObjectType,
  type BoilerroomDefectType,
  type BoilerroomObjectType,
  type TranslateDefect,
} from "@eisox/building-management-system";

import { m } from "~/paraglide/messages";
import { getLocale } from "~/paraglide/runtime";

/** Which equipment a defect dialog is opened from — decides the location column and the filtering. */
export type DefectScope = "boilerRoom" | "airHandlerUnit";

export interface DefectHistoryRow extends ModuleDefectHistoryEntry {
  /** Human label of where the fault sits, or `undefined` when it can't be placed. */
  location?: string;
  message: string;
}

const toTimestamp = (value: string | null | undefined) => {
  if (!value) return Number.NEGATIVE_INFINITY;

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

const isActive = (entry: Pick<ModuleDefectHistoryEntry, "end">) => entry.end == null;

/** Active faults first, then most recently ended; ties broken by the most recent start. */
export const compareDefectHistoryByEndDefault = (
  a: Pick<ModuleDefectHistoryEntry, "begin" | "end">,
  b: Pick<ModuleDefectHistoryEntry, "begin" | "end">,
): number => {
  const aIsActive = isActive(a);
  const bIsActive = isActive(b);

  if (aIsActive !== bIsActive) return aIsActive ? -1 : 1;

  if (aIsActive) {
    return toTimestamp(b.begin) - toTimestamp(a.begin);
  }

  const endComparison = toTimestamp(b.end) - toTimestamp(a.end);
  if (endComparison !== 0) return endComparison;

  return toTimestamp(b.begin) - toTimestamp(a.begin);
};

/**
 * Module- and gateway-scoped faults are shown whatever the dialog was opened from — they affect the
 * whole box. Everything else must belong to the equipment family being looked at.
 */
export const filterDefectsByScope = (
  entries: ModuleDefectHistoryEntry[],
  scope: DefectScope,
): ModuleDefectHistoryEntry[] =>
  entries.filter(entry => {
    if (entry.objectType === ModuleDefectObjectType.MODULE || entry.objectType === ModuleDefectObjectType.GATEWAY)
      return true;
    return scope === "boilerRoom" ? !!entry.boilerroomId : !!entry.airHandlerUnitId;
  });

const getLocation = (entry: ModuleDefectHistoryEntry, scope: DefectScope): string | undefined => {
  if (scope === "airHandlerUnit") return entry.airHandlerUnitName ?? "-";

  const { boilerroomName, boilerName, heatingNetworkName } = entry;
  if (!boilerroomName && !boilerName && !heatingNetworkName) return undefined;
  return `${boilerroomName ?? "-"}/${boilerName ?? heatingNetworkName ?? "-"}`;
};

const getMessage = (entry: ModuleDefectHistoryEntry): string => {
  const defect = {
    defectType: entry.defectType as BoilerroomDefectType & AirHandlerUnitDefectType,
    objectType: entry.objectType as BoilerroomObjectType & AirHandlerUnitObjectType,
  };

  if (isValidBoilerroomDefect(defect) || isValidAirHandlerUnitDefect(defect)) {
    return translateDefect({ ...(entry as unknown as TranslateDefect), language: getLocale() });
  }
  return m.defect_history_no_data();
};

export const toDefectHistoryRows = (entries: ModuleDefectHistoryEntry[], scope: DefectScope): DefectHistoryRow[] =>
  entries.map(entry => ({ ...entry, location: getLocation(entry, scope), message: getMessage(entry) }));
